import forEach from 'lodash/forEach';

import { isTouch, supportsPassiveEvents } from 'atg/utils/device';
import * as Utils from 'atg/utils/Utils';
import { getScrollPosition, setScrollPosition } from 'shared/utils/dom';

const $ = window.$;

// Maximum allowed change in event position between press and release on a grid item.
// If the change is greater it will be ignored.
const EVENT_TRAVEL_BOUNDS = 30;

let current = null;
let hasPendingRowUpdate = false;
let gridProps;
let webkitHackEnabled = false;

export default class GridCellInteractionManager {
  constructor(props) {
    gridProps = props;

    // HACK: Webkit has some bugs with touchmove that stops event.preventDefault()
    // from working correctly in dynamically added handlers.
    // To get arund this we have this always listening event handler (inspired by
    // react-beautiful-dnd).
    // iOS: 10 (fixed in 10.3?)
    // https://openradar.appspot.com/28479522
    // https://bugs.webkit.org/show_bug.cgi?id=163207
    // https://github.com/metafizzy/flickity/issues/457#issuecomment-254501356
    // iOS: 11.3
    // https://bugs.webkit.org/show_bug.cgi?id=184250
    if ('ontouchstart' in window && supportsPassiveEvents()) {
      window.addEventListener('touchmove', this._onGlobalTouchmove, {
        passive: false,
      });
    }
  }

  destroy() {
    if ('ontouchstart' in window && supportsPassiveEvents()) {
      window.removeEventListener('touchmove', this._onGlobalTouchmove, {
        passive: false,
      });
    }
  }

  _onGlobalTouchmove = event => {
    if (!webkitHackEnabled) {
      return;
    }

    // event.preventDefault() worked in dynamic handler
    if (event.defaulPrevented) {
      return;
    }

    // event.preventDefault() did not work, fix it!
    event.preventDefault();
  };

  handleCellPress(props) {
    if (current) {
      return;
    }

    current = new Interaction(props);
  }

  isActiveInRow(row) {
    return current && current.isActiveInRow(row);
  }

  setPendingRowUpdate(value) {
    hasPendingRowUpdate = value;
  }
}

class Interaction {
  constructor({ event, $cell, gridSize, isBlockedFn, showAlertWhenBlockedFn }) {
    this._$cell = $cell;
    this._gridSize = gridSize;
    this._isBlockedFn = isBlockedFn;
    this._showAlertWhenBlockedFn = showAlertWhenBlockedFn;

    const gridPosition = gridProps.getCellPosition($cell);

    this._isDragStarted = false;
    this._hasDragged = false;
    this._isAutoScrolling = false;
    this._$clone = null;
    this._cloneX = false;
    this._row = null;
    this._lastColumn = null;
    this._timers = {};
    this._startTime = Date.now();
    this._initialPagePosition = this._lastPagePosition = Utils.getEventPosition(
      event,
      'page',
    );
    this._initialClientPosition = Utils.getEventPosition(event, 'client');
    this._initialScrollPosition = getScrollPosition(
      gridProps.$scrollContainer.get(0),
    );
    this._scrollOffset = 0;
    this._leftEdgeOffset = this._initialPagePosition.x - $cell.position().left; // Distance between where event occured and the cell's left edge
    this._$row = $cell.closest('.row');
    this._$cells = $cell.closest('.row').find('.cell.item');
    this._row = gridPosition.row;
    this._lastColumn = this._initialColumn = gridPosition.column;

    this._columnsPositions = [];

    for (let column = 0; column < this._$cells.length; column++) {
      this._columnsPositions[column] = gridProps.grid.getColumnLeft(column);
    }

    this._onRelease = this._onRelease.bind(this);
    this._onMove = this._onMove.bind(this);
    this._dosScrollWhileDragging = this._dosScrollWhileDragging.bind(this);

    this._timers['startDrag'] = window.setTimeout(() => {
      this._onStartDragTimer();
    }, 250);

    const $root = $(window);
    $root.event('up', this._onRelease);
    $root.event('move', this._onMove);
  }

  isActiveInRow(row) {
    return this._row === row;
  }

  _destroy() {
    const $root = $(window);
    $root.unevent('up', this._onRelease);
    $root.unevent('move', this._onMove);
    window.cancelAnimationFrame(this._timers['autoScroll']);

    forEach(this._timers, value => {
      if (value !== 'autoScroll') {
        window.clearTimeout(value);
      }
    });
    this._timers = {};

    const row = this._row;
    this._row = null;
    if (hasPendingRowUpdate) {
      gridProps.grid.updateRow(row);
    }

    current = null;
  }

  _onMove(event) {
    this._cancelScrollWhileDragging();

    const pagePos = Utils.getEventPosition(event, 'page');

    if (Utils.positionsAreFurther(pagePos, this._initialPagePosition, 2)) {
      this._cancelStartDragTimer();
      this._hasDragged = true;
    }

    if (this._isDragStarted) {
      event.preventDefault();
      webkitHackEnabled = true;

      if (!this._cloneX) {
        this._cloneX = pagePos.x - this._leftEdgeOffset;
      } else {
        this._cloneX +=
          pagePos.x - this._lastPagePosition.x + this._scrollOffset;
      }

      this._scrollOffset = 0;

      this._updateDropTarget(this._cloneX);

      this._scrollBounds = {
        left: 0,
        right: gridProps.$scrollContainer.width(),
        maxLeft: 0,
        maxRight:
          gridProps.grid.getColumnRight(this._gridSize.columns - 1) -
          gridProps.$scrollContainer.width(),
      };

      let autoScrollDirection;
      if (pagePos.x < this._scrollBounds.left + 10) {
        autoScrollDirection = 'left';
      } else if (pagePos.x > this._scrollBounds.right - 10) {
        autoScrollDirection = 'right';
      }

      if (autoScrollDirection) {
        this._autoScrollStartScrollLeft =
          gridProps.$scrollContainer.scrollLeft();
        this._autoScrollDirection = autoScrollDirection;
        this._isAutoScrolling = true;
        this._timers['autoScroll'] = requestAnimationFrame(
          this._dosScrollWhileDragging,
        );
      } else {
        this._$clone.transform(`translateX(${this._cloneX}px) translateZ(0)`);
      }
    } else {
      // Do drag scroll on non touch devices
      if (!isTouch()) {
        const clientPosition = Utils.getEventPosition(event, 'client');
        const dX = this._initialClientPosition.x - clientPosition.x;
        const dY = this._initialClientPosition.y - clientPosition.y;

        setScrollPosition(
          gridProps.$scrollContainer.get(0),
          this._initialScrollPosition.x + dX,
          this._initialScrollPosition.y + dY,
        );
      }
    }

    this._lastPagePosition = pagePos;
  }

  _updateDropTarget(cloneX) {
    let closestColumn;
    let closestDistance;

    for (let column = 0; column < this._columnsPositions.length; column++) {
      const columnX = this._columnsPositions[column];
      const distance = Math.abs(columnX - cloneX);

      if (column === 0 || distance < closestDistance) {
        closestColumn = column;
        closestDistance = distance;
      }
    }

    if (this._lastColumn !== closestColumn) {
      let afterIndex = closestColumn;

      if (this._lastColumn <= closestColumn) {
        afterIndex++;
      }

      this._moveCell(afterIndex);
      this._lastColumn = closestColumn;
    }
  }

  _moveCell(afterIndex) {
    const $cellBefore = this._$row.find(`.cell:eq(${afterIndex})`);
    this._$cell.insertAfter($cellBefore);
  }

  _dosScrollWhileDragging() {
    if (this._isAutoScrolling) {
      let n = Math.floor(1 + this._numScrollingPasses * 0.03);
      if (this._autoScrollDirection === 'left') {
        n *= -1;
      }
      n += gridProps.$scrollContainer.scrollLeft();
      this._scrollOffset = n - this._autoScrollStartScrollLeft;
      const n2 = this._cloneX + this._scrollOffset;
      this._$clone.transform(`translateX(${n2}px) translateZ(0)`);
      this._updateDropTarget(n2);

      if (
        (n < this._scrollBounds.maxLeft &&
          this._autoScrollDirection === 'left') ||
        (n > this._scrollBounds.maxRight &&
          this._autoScrollDirection === 'right')
      ) {
        this._cancelScrollWhileDragging();
        return;
      }

      gridProps.$scrollContainer.scrollLeft(n);
      this._numScrollingPasses++;
      this._timers['autoScroll'] = requestAnimationFrame(
        this._dosScrollWhileDragging,
      );
    }
  }

  _cancelScrollWhileDragging() {
    this._isAutoScrolling = false;
    this._numScrollingPasses = 0;
  }

  _onStartDragTimer() {
    this._cancelStartDragTimer();

    // Handle touch scroll: Exit if user has moved too far away from the pressed position
    const movedPos = this._lastPagePosition;
    const distanceTravelled = Utils.calcDistance(
      this._initialPagePosition.x,
      this._initialPagePosition.y,
      movedPos.x,
      movedPos.y,
    );
    if (distanceTravelled > EVENT_TRAVEL_BOUNDS) {
      this._destroy();
      return;
    }

    this._isDragStarted = true;

    gridProps.grid.highlightRow(gridProps.getCellPosition(this._$cell).row);

    const $clone = (this._$clone = this._$cell.clone());

    this._$row.append($clone);

    $clone.addClass('drag-copy');
    $clone.transform(
      `translateX(${this._$cell.position().left}px) translateZ(0)`,
    );

    this._timers['enlarge'] = window.setTimeout(() => {
      this._$clone.addClass('enlarged');
    }, 50);

    this._$cell.addClass('drag');
  }

  _cancelStartDragTimer() {
    if (this._timers['startDrag']) {
      window.clearTimeout(this._timers['startDrag']);
      delete this._timers['startDrag'];
      return true;
    } else {
      return false;
    }
  }

  _onRelease(event) {
    event.preventDefault();
    this._cancelScrollWhileDragging();
    webkitHackEnabled = false;

    const $root = $(window);
    $root.unevent('move', this._onMove);

    if (this._isDragStarted) {
      this._$clone.removeClass('enlarged');
      this._$clone.addClass('animated');

      this._$clone.transform(
        `translateX(${gridProps.grid.getColumnLeft(
          this._isBlockedFn() ? this._initialColumn : this._lastColumn,
        )}px) translateZ(0)`,
      );

      this._timers['finishSort'] = window.setTimeout(() => {
        this._finishSortInteraction();
      }, 100);
    } else {
      // Handle touch scroll: Exit if user has moved too far away from the pressed position
      const eventPos = Utils.getEventPosition(event, 'page');
      const distanceTravelled = Utils.calcDistance(
        this._initialPagePosition.x,
        this._initialPagePosition.y,
        eventPos.x,
        eventPos.y,
      );
      if (distanceTravelled > EVENT_TRAVEL_BOUNDS) {
        this._destroy();
        return;
      }

      const didTap =
        this._cancelStartDragTimer() ||
        (!this._hasDragged && Date.now() - this._startTime < 500);

      if (didTap) {
        if (this._timers['doubleTap']) {
          window.clearTimeout(this._timers['doubleTap']);
          gridProps.onDoubleTap(this._$cell, event);
          this._destroy();
        } else {
          this._timers['doubleTap'] = window.setTimeout(() => {
            gridProps.onTap(this._$cell, event);
            this._destroy();
          }, 200);
        }
      } else {
        this._destroy();
      }
    }
  }

  _finishSortInteraction() {
    gridProps.grid.closeHighlight();
    this._$cell.removeClass('drag');
    this._$clone.remove();

    const $cellsBefore = this._$cells;
    const $cellsAfter = this._$row.find('.cell.item').not(this._$clone);
    let anyIndexChange = false;
    const indexMap = [];

    for (let indexAfter = 0; indexAfter < $cellsAfter.length; indexAfter++) {
      const $cellAfter = $cellsAfter.eq(indexAfter);
      const indexBefore = $cellsBefore.index($cellAfter);
      indexMap.push(indexBefore);
      if (indexBefore !== indexAfter) {
        anyIndexChange = true;
      }
    }

    if (anyIndexChange) {
      if (this._showAlertWhenBlockedFn()) {
        if (this._lastColumn <= this._initialColumn) {
          this._moveCell(this._initialColumn + 1);
        } else {
          this._moveCell(this._initialColumn);
        }
      } else {
        const row = this._$row.index();
        gridProps.onSort(row, indexMap);
      }
    }
    this._destroy();
  }
}
